<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { bsonId } from "@/common/utils/common";
import { QuillEditor, loadQuill } from "@vueup/vue-quill";
import Delta from "quill-delta";
import { styleZoom } from "@/common/utils/style";
import "@vueup/vue-quill/dist/vue-quill.snow.css";

const emit = defineEmits(["update"]);

const props = withDefaults(
  defineProps<{
    modelValue: any;
    floating?: boolean;
    selected?: boolean;
    style?: any;
    toolbarId?: string;
    zoom?: number;
    showAlign?: boolean;
    showSize?: boolean;
  }>(),
  {
    floating: false,
    showAlign: false,
    showSize: false,
    selected: false,
    toolbarId: "custom-toolbar",
    zoom: 1,
  },
);

const internalId = bsonId();

const id = computed(() => `${props.toolbarId}-${internalId}`);

const editorOptions = {
  modules: {
    toolbar: `#${id.value}`,
  },
  placeholder: "Digite seu texto aqui...",
  theme: "snow",
};

const editorContent = ref<any>(new Delta());
const quillEditorRef = ref<InstanceType<typeof QuillEditor> | null>(null);

async function initialize() {
  try {
    const Quill = await loadQuill();

    const Size = Quill.import("attributors/style/size") as any;

    Size.whitelist = [
      "0.375em", // 6px
      "0.5em", // 8px
      "0.625em", // 10px
      "0.6875em", // 11px
      "0.75em", // 12px
      "0.8125em", // 13px
      "0.875em", // 14px
      "0.9375em", // 15px
      "1em", // 16px
      "1.0625em", // 17px
      "1.125em", // 18px
      "1.25em", // 20px
      "1.375em", // 22px
      "1.5em", // 24px
      "1.625em", // 26px
      "1.75em", // 28px
      "1.875em", // 30px
    ];

    Quill.register(Size, true);
  } catch (error) {
    console.error("Erro ao inicializar Quill:", error);
  }
}

function onEditorReady(_quill: any) {
  // O Quill está garantidamente carregado aqui.
  // Se precisar de algum ajuste pós-ready, faça neste bloco.
}

function update(value: any) {
  if (value === props.modelValue) {
    return;
  }

  emit("update", value);
}

function processDelta(value: any): any {
  if (!value) {
    return new Delta();
  }

  let parsedValue = value;

  if (typeof value === "string" && value.trim().startsWith("{")) {
    try {
      parsedValue = JSON.parse(value);
    } catch (e) {
      console.error("Erro ao fazer parse da string Delta:", e);
      return new Delta();
    }
  }

  if (parsedValue && typeof parsedValue === "object" && "ops" in parsedValue) {
    return new Delta(parsedValue);
  }

  return new Delta();
}

watch(
  () => props.modelValue,
  (newValue) => {
    editorContent.value = processDelta(newValue);
  },
);

// manda para o final do texto
watch(
  () => props.selected,
  (active) => {
    if (active) {
      requestAnimationFrame(() => {
        const editor = quillEditorRef.value?.getQuill();
        if (editor) {
          const length = editor.getLength();
          editor.setSelection(length, 0);
        }
      });
    }
  },
);

onMounted(async () => {
  await initialize();
  editorContent.value = processDelta(props.modelValue);
});
</script>

<template>
  <div class="custom-quill">
    <div
      :id="id"
      class="custom-toolbar"
      :class="{
        floating: floating,
        selected: selected,
      }"
    >
      <span class="ql-formats">
        <button class="ql-bold"></button>
        <button class="ql-italic"></button>
        <button class="ql-underline"></button>
      </span>

      <span class="ql-formats">
        <template v-if="showSize">
          <select class="ql-size">
            <option value="0.375em">6px</option>
            <option value="0.5em">8px</option>
            <option value="0.625em">10px</option>
            <option value="0.6875em">11px</option>
            <option value="0.75em">12px</option>
            <option value="0.8125em">13px</option>
            <option value="0.875em">14px</option>
            <option value="0.9375em">15px</option>
            <option value="1em">16px</option>
            <option value="1.0625em">17px</option>
            <option value="1.125em">18px</option>
            <option value="1.25em">20px</option>
            <option value="1.375em">22px</option>
            <option value="1.5em">24px</option>
            <option value="1.625em">26px</option>
            <option value="1.75em">28px</option>
            <option value="1.875em">30px</option>
          </select>
        </template>

        <template v-if="showAlign">
          <select class="ql-align">
            <option selected></option>
            <option value="center"></option>
            <option value="right"></option>
            <option value="justify"></option>
          </select>
        </template>

        <select class="ql-color"></select>

        <select class="ql-background"></select>
      </span>

      <span class="ql-formats">
        <button class="ql-list" value="ordered"></button>
        <button class="ql-list" value="bullet"></button>
      </span>

      <span class="ql-formats">
        <button class="ql-clean"></button>
      </span>
    </div>

    <QuillEditor
      ref="quillEditorRef"
      v-model:content="editorContent"
      :options="editorOptions"
      content-type="delta"
      theme="snow"
      @ready="onEditorReady"
      @update:content="update"
      :style="styleZoom(style, zoom)"
    />
  </div>
</template>

<style lang="scss" scoped>
.custom-quill {
  position: relative;

  .custom-toolbar {
    position: relative;
    border-top-left-radius: 0.375em;
    border-top-right-radius: 0.375em;
    background-color: #f7f7f7;
    padding: 0.5em;
    border: 1px solid #ccc;
    border-bottom: none;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    z-index: -99;
    opacity: 0;

    &.floating {
      position: fixed;
      top: 50px;
      left: 300px;
      border-radius: 0px;
      width: calc(100% - 300px);
      padding: 6px;
      border-bottom: 1px solid #969696;
    }

    &.selected {
      z-index: 99;
      opacity: 1;
    }

    .ql-formats {
      margin-right: 0px;
      display: flex;
      align-items: center;
      gap: 4px;

      &:last-child {
        margin-right: 0;
      }

      .ql-size {
        width: 55px;

        .ql-picker-label {
          font-size: 12px;
        }
      }

      .ql-font {
        width: 90px;
      }
    }

    // Botões da toolbar
    button {
      position: relative;
      width: 24px;
      height: 24px;
      border: none;
      background: transparent;
      cursor: pointer;
      border-radius: 4px;
      transition: background-color 0.2s;
      font-size: 12px;

      &:hover {
        background-color: rgba(0, 0, 0, 0.06);
      }

      &.ql-active {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }

    // Pickers (dropdowns)
    :deep(.ql-picker) {
      height: 28px;

      .ql-picker-label {
        border: 1px solid #ccc;
        border-radius: 0;
        padding: 3px 0.375em;
        background: #fff;
        transition: all 0.2s;
        font-size: 12px;

        &:hover {
          border-color: #999;
          background-color: #f9f9f9;
        }
      }

      &.ql-expanded .ql-picker-label {
        border-color: #06c;
        background-color: #fff;
      }

      .ql-picker-options {
        position: absolute;
        border: 1px solid #ccc;
        border-radius: 0;
        box-shadow: 0 2px 0.5em rgba(0, 0, 0, 0.15);
        background: #fff;
        margin-top: 4px;
        z-index: 99;
        right: 0;
      }

      .ql-picker-item {
        padding: 4px;

        &:hover {
          background-color: #f0f0f0;
        }

        &.ql-selected {
          background-color: #e6f2ff;
          color: #06c;
        }
      }
    }
  }

  :deep(.ql-editor) {
    font-size: 1em;
    line-height: 1.5;
    padding: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    min-height: 0.625em;

    .ql-font-arial,
    *[class*="ql-font-arial"] {
      font-family: Arial, sans-serif !important;
    }

    .ql-font-times-new-roman,
    *[class*="ql-font-times-new-roman"] {
      font-family: "Times New Roman", serif !important;
    }

    .ql-font-courier,
    *[class*="ql-font-courier"] {
      font-family: "Courier New", monospace !important;
    }

    .ql-font-georgia,
    *[class*="ql-font-georgia"] {
      font-family: Georgia, serif !important;
    }

    .ql-font-sans-serif,
    *[class*="ql-font-sans-serif"] {
      font-family: sans-serif !important;
    }

    &.ql-blank::before {
      left: 0.625em;
      right: 0.625em;
      font-style: normal;
      font-size: 1em;
      color: #999;
    }
  }

  :deep(.ql-picker.ql-font) {
    .ql-picker-label[data-value="arial"]::before,
    .ql-picker-item[data-value="arial"]::before {
      content: "Arial";
      font-family: Arial, sans-serif;
    }

    .ql-picker-label[data-value="times-new-roman"]::before,
    .ql-picker-item[data-value="times-new-roman"]::before {
      content: "Times";
      font-family: "Times New Roman", serif;
    }

    .ql-picker-label[data-value="courier"]::before,
    .ql-picker-item[data-value="courier"]::before {
      content: "Courier";
      font-family: "Courier New", monospace;
    }

    .ql-picker-label[data-value="georgia"]::before,
    .ql-picker-item[data-value="georgia"]::before {
      content: "Georgia";
      font-family: Georgia, serif;
    }

    .ql-picker-label[data-value="sans-serif"]::before,
    .ql-picker-item[data-value="sans-serif"]::before {
      content: "Sans Serif";
      font-family: sans-serif;
    }

    .ql-picker-label:not([data-value])::before {
      content: "Fonte";
    }
  }

  :deep(.ql-picker.ql-size) {
    .ql-picker-label:not([data-value])::before {
      content: "Tamanho";
    }
  }

  :deep(.ql-container.ql-snow) {
    font-size: 1em;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top: none;
    font-family: inherit;
  }

  :deep(.ql-editor p) {
    margin: 0;
    margin-block-end: 0.3125em;
  }
}
</style>
